/**
 * O ponto de entrada.
 *
 * A ordem dos imports de CSS e normativa (`@farmacia/ui/src/index.ts`):
 * primitivos (camada 1, gerada) -> semantico (camada 2, gerado) -> base (regras de documento) ->
 * balcao (componentes) -> o CSS do app. Inverter faz o token do app perder para o do pacote, ou
 * o contrario, dependendo da linha — e o sintoma e "a cor certa em oito lugares e errada em um".
 */

import '@farmacia/ui/css/primitives.css';
import '@farmacia/ui/css/semantic.css';
import '@farmacia/ui/css/base.css';
import '@farmacia/ui/css/balcao.css';
import './estilos/app.css';

import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

import { Provedores } from './app/providers.jsx';
import { ProvedorDePwa } from './pwa/contexto.jsx';
import { useRegistroDoSw } from './pwa/useRegistroDoSw.js';
import { routeTree } from './routeTree.gen';

const router = createRouter({
  routeTree,
  // `PWA §11.5` — prefetch em `pointerdown`/`touchstart`. **Nunca** em hover nem por
  // visibilidade: o conselho padrao de pre-carregar links visiveis gasta o dado de quem paga
  // por MB para adivinhar navegacao.
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * Liga o registro do service worker ao contexto de PWA.
 *
 * A decisao de registrar (ou nao) mora em `pwa/useRegistroDoSw.ts`, junto com o motivo: em
 * `vite dev` nao existe `/sw.js` — o `vite.config.ts` so o gera quando `SW_EM_DEV` e verdadeiro
 * —, e tentar registrar o `index.html` como worker eram 2 erros de console por carga.
 */
function Aplicacao(): ReactNode {
  const sw = useRegistroDoSw();

  return (
    <ProvedorDePwa
      temVersaoNova={sw.temVersaoNova}
      aoAplicarAtualizacao={sw.aplicarAtualizacao}
    >
      <Provedores>
        <RouterProvider router={router} />
      </Provedores>
    </ProvedorDePwa>
  );
}

const raiz = document.getElementById('raiz');
if (raiz === null) throw new Error('Elemento #raiz ausente no index.html.');

createRoot(raiz).render(
  <StrictMode>
    <Aplicacao />
  </StrictMode>,
);
